import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useFormat } from "../context/FormatContext";
import axios from "axios";
import "../assets/services.css";

const SubProducts: React.FC = () => {
  const [subProducts, setSubProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [brands, setBrands] = useState<any[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<number[]>([]);
  const [selectedSliderBrandId, setSelectedSliderBrandId] = useState<
    number | null
  >(null);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedPromotions, setSelectedPromotions] = useState<string[]>([]);
  const [stockFilter, setStockFilter] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const brandSliderRef = useRef<HTMLDivElement | null>(null);
  const isBrandHoveredRef = useRef(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const subCategoryId = params.get("id");
  const subcategoryName = params.get("name");
  const { addToCart } = useCart();
  const { formatCurrency, toSafeNumber, toCaptionCase } = useFormat();

  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const scrollBrandSlider = (direction: "left" | "right") => {
    if (!brandSliderRef.current) return;

    brandSliderRef.current.scrollBy({
      left: direction === "left" ? -260 : 260,
      behavior: "smooth",
    });
  };

  const brandSliderItems = brands.length > 1 ? [...brands, ...brands] : brands;

  const selectedSliderBrand = useMemo(() => {
    if (!selectedSliderBrandId) return null;

    return brands.find(
      (brand: any) => Number(brand.id) === Number(selectedSliderBrandId),
    );
  }, [brands, selectedSliderBrandId]);

  const getProductImageUrl = (product: any) => {
    if (!product?.image_path) return "";

    return `${IMAGE_URL}/uploads/products/${product.id}/${product.image_path}`;
  };

  const getDiscountPercent = (product: any) => {
    const previousPrice = toSafeNumber(product.previousPrice);
    const activePrice = toSafeNumber(product.active_price);

    if (!previousPrice || !activePrice || activePrice >= previousPrice) {
      return null;
    }

    return Math.round(((previousPrice - activePrice) / previousPrice) * 100);
  };

  const toggleNumberFilter = (
    value: number,
    setter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const toggleTextFilter = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const brandOptions = useMemo(() => {
    const brandMap = new Map<number, any>();

    subProducts.forEach((product: any) => {
      if (!product.brand_id || !product.brand_name) return;

      const brandId = Number(product.brand_id);

      if (!brandMap.has(brandId)) {
        brandMap.set(brandId, {
          id: brandId,
          name: product.brand_name,
        });
      }
    });

    return Array.from(brandMap.values());
  }, [subProducts]);

  const conditionOptions = useMemo(() => {
    const values = new Set<string>();

    subProducts.forEach((product: any) => {
      if (product.product_condition) {
        values.add(String(product.product_condition));
      }
    });

    return Array.from(values);
  }, [subProducts]);

  const stockOptions = useMemo(() => {
    const values = new Set<string>();

    subProducts.forEach((product: any) => {
      if (product.stock_status) {
        values.add(String(product.stock_status));
      }
    });

    return Array.from(values);
  }, [subProducts]);

  const filteredSubProducts = useMemo(() => {
    let products = [...subProducts];

    if (productSearch.trim()) {
      const keyword = productSearch.trim().toLowerCase();

      products = products.filter((product: any) =>
        `${product.productName || ""} ${product.shortDescription || ""} ${
          product.brand_name || ""
        }`
          .toLowerCase()
          .includes(keyword),
      );
    }

    if (selectedBrandIds.length > 0) {
      products = products.filter((product: any) =>
        selectedBrandIds.includes(Number(product.brand_id)),
      );
    }

    if (selectedConditions.length > 0) {
      products = products.filter((product: any) =>
        selectedConditions.includes(String(product.product_condition)),
      );
    }

    if (stockFilter !== "all") {
      products = products.filter(
        (product: any) => String(product.stock_status) === stockFilter,
      );
    }

    if (minPrice !== "") {
      products = products.filter(
        (product: any) =>
          toSafeNumber(product.active_price) >= toSafeNumber(minPrice),
      );
    }

    if (maxPrice !== "") {
      products = products.filter(
        (product: any) =>
          toSafeNumber(product.active_price) <= toSafeNumber(maxPrice),
      );
    }

    if (selectedPromotions.length > 0) {
      products = products.filter((product: any) => {
        const checks = {
          featured: Number(product.is_featured) === 1,
          promoted: Number(product.is_promoted) === 1,
          discount: Boolean(getDiscountPercent(product)),
        };

        return selectedPromotions.some(
          (promo) => checks[promo as keyof typeof checks],
        );
      });
    }

    if (sortBy === "latest") {
      products.sort(
        (a: any, b: any) => toSafeNumber(b.id) - toSafeNumber(a.id),
      );
    } else if (sortBy === "top_sales") {
      products.sort(
        (a: any, b: any) =>
          toSafeNumber(b.total_sold) - toSafeNumber(a.total_sold),
      );
    } else if (sortBy === "price_low") {
      products.sort(
        (a: any, b: any) =>
          toSafeNumber(a.active_price) - toSafeNumber(b.active_price),
      );
    } else if (sortBy === "price_high") {
      products.sort(
        (a: any, b: any) =>
          toSafeNumber(b.active_price) - toSafeNumber(a.active_price),
      );
    } else {
      products.sort((a: any, b: any) => {
        const bScore = toSafeNumber(b.total_sold) + toSafeNumber(b.view_count);
        const aScore = toSafeNumber(a.total_sold) + toSafeNumber(a.view_count);

        return bScore - aScore;
      });
    }

    return products;
  }, [
    subProducts,
    productSearch,
    selectedBrandIds,
    selectedConditions,
    selectedPromotions,
    stockFilter,
    minPrice,
    maxPrice,
    sortBy,
  ]);

  const clearAllFilters = () => {
    setProductSearch("");
    setSelectedBrandIds([]);
    setSelectedConditions([]);
    setSelectedPromotions([]);
    setStockFilter("all");
    setMinPrice("");
    setMaxPrice("");
  };

  const renderBrandFilters = (prefix: string) => (
    <>
      {brandOptions.length > 0 ? (
        brandOptions.map((brand: any) => (
          <label
            className="sp-filter-check"
            key={`${prefix}-brand-${brand.id}`}
          >
            <input
              type="checkbox"
              checked={selectedBrandIds.includes(Number(brand.id))}
              onChange={() =>
                toggleNumberFilter(Number(brand.id), setSelectedBrandIds)
              }
            />
            <span>{brand.name}</span>
          </label>
        ))
      ) : (
        <p className="sp-filter-empty">No brand found</p>
      )}
    </>
  );

  const renderConditionFilters = (prefix: string) => (
    <>
      {conditionOptions.length > 0 ? (
        conditionOptions.map((condition) => (
          <label
            className="sp-filter-check"
            key={`${prefix}-condition-${condition}`}
          >
            <input
              type="checkbox"
              checked={selectedConditions.includes(condition)}
              onChange={() =>
                toggleTextFilter(condition, setSelectedConditions)
              }
            />
            <span>{toCaptionCase(condition)}</span>
          </label>
        ))
      ) : (
        <p className="sp-filter-empty">No condition found</p>
      )}
    </>
  );

  const renderPromotionFilters = (prefix: string) => (
    <>
      {[
        { label: "Featured Products", value: "featured" },
        { label: "Promoted Products", value: "promoted" },
        { label: "Discount Products", value: "discount" },
      ].map((item) => (
        <label
          className="sp-filter-check"
          key={`${prefix}-promo-${item.value}`}
        >
          <input
            type="checkbox"
            checked={selectedPromotions.includes(item.value)}
            onChange={() => toggleTextFilter(item.value, setSelectedPromotions)}
          />
          <span>{item.label}</span>
        </label>
      ))}
    </>
  );

  useEffect(() => {
    const slider = brandSliderRef.current;

    if (!slider || brands.length === 0) return;

    let animationFrameId: number;
    let lastTime: number | null = null;

    const speed = 0.12;

    const autoScroll = (currentTime: number) => {
      if (lastTime === null) {
        lastTime = currentTime;
      }

      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      if (
        !isBrandHoveredRef.current &&
        slider.scrollWidth > slider.clientWidth
      ) {
        slider.scrollLeft += deltaTime * speed;

        const halfScrollWidth = slider.scrollWidth / 2;

        if (slider.scrollLeft >= halfScrollWidth) {
          slider.scrollLeft -= halfScrollWidth;
        }
      }

      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [brands.length]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_LYRAZONE_BASE_URL}/brands`,
        );

        const brandRows = response.data.brands || [];

        setBrands(
          brandRows
            .filter((brand: any) => brand.status !== "inactive")
            .map((brand: any) => ({
              id: brand.id,
              name: brand.name,
              image: brand.image,
            })),
        );
      } catch (err) {
        console.error("Fetch Brands Error:", err);
      }
    };

    fetchBrands();
  }, []);

  // useEffect(() => {
  //   const fetchSubProduct = async () => {
  //     try {
  //       const response = await axios.get(
  //         `${import.meta.env.VITE_LYRAZONE_BASE_URL}/subproducts/${subCategoryId}`,
  //       );
  //       setSubProducts(response.data.subProduct);
  //     } catch (err) {
  //       console.error(err);
  //     }
  //   };

  //   fetchSubProduct();
  // }, []);

  const resetProductFilters = () => {
    setProductSearch("");
    setSelectedBrandIds([]);
    setSelectedConditions([]);
    setSelectedPromotions([]);
    setStockFilter("all");
    setMinPrice("");
    setMaxPrice("");
  };

  const fetchSubProductsBySubCategory = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/subproducts/${subCategoryId}`,
      );

      setSubProducts(response.data.subProduct || []);
      setSelectedSliderBrandId(null);
    } catch (err) {
      console.error("Fetch Subcategory Products Error:", err);
    }
  };

  const fetchProductsByBrand = async (brandId: number) => {
    try {
      resetProductFilters();

      const response = await axios.get(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/brand-products/${brandId}`,
      );

      setSubProducts(response.data.subProduct || []);
      setSelectedSliderBrandId(brandId);
    } catch (err) {
      console.error("Fetch Brand Products Error:", err);
    }
  };

  const handleBrandSliderClick = async (brandId: number) => {
    if (selectedSliderBrandId === brandId) {
      resetProductFilters();
      await fetchSubProductsBySubCategory();
      return;
    }

    await fetchProductsByBrand(brandId);
  };

  const clearSelectedSliderBrand = async () => {
    if (!selectedSliderBrandId) return;

    resetProductFilters();
    await fetchSubProductsBySubCategory();
  };

  useEffect(() => {
    if (subCategoryId) {
      fetchSubProductsBySubCategory();
    }
  }, [subCategoryId]);

  const productData = (productId: any) => {
    window.location.href = `/singleproduct?id=${productId}`;
  };

  return (
    <>
      <div id="home-p" className="home-p pages-head11 text-center" />

      <section
        id="business-growth-p1"
        className="hardware-subcategory-strip-section hardware-brand-strip-section"
      >
        <div
          className="hardware-subcategory-strip-wrap"
          onMouseEnter={() => {
            isBrandHoveredRef.current = true;
          }}
          onMouseLeave={() => {
            isBrandHoveredRef.current = false;
          }}
        >
          <button
            type="button"
            className="hardware-subcategory-arrow hardware-subcategory-arrow-left"
            onClick={() => scrollBrandSlider("left")}
            aria-label="Scroll brands left"
          >
            ‹
          </button>

          <div ref={brandSliderRef} className="hardware-subcategory-strip">
            {brandSliderItems.length > 0 ? (
              brandSliderItems.map((brand: any, index: number) => (
                // <button
                //   key={`${brand.id}-${index}`}
                //   type="button"
                //   className="hardware-subcategory-item hardware-brand-item"
                //   title={brand.name}
                // >
                <button
                  key={`${brand.id}-${index}`}
                  type="button"
                  className={`hardware-subcategory-item hardware-brand-item ${
                    selectedSliderBrandId === Number(brand.id)
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() => handleBrandSliderClick(Number(brand.id))}
                  title={brand.name}
                >
                  <span className="hardware-subcategory-icon hardware-brand-icon">
                    {brand.image ? (
                      <img
                        src={`${
                          import.meta.env.VITE_LYRAZONE_IMAGE_URL
                        }/uploads/brands/${brand.image}`}
                        alt={brand.name}
                        className="hardware-brand-logo"
                      />
                    ) : (
                      <span className="hardware-brand-placeholder">
                        {brand.name?.charAt(0)}
                      </span>
                    )}
                  </span>

                  <span className="hardware-subcategory-name">
                    {brand.name}
                  </span>
                </button>
              ))
            ) : (
              <div className="hardware-brand-empty-title">
                <h1>{subcategoryName}</h1>
              </div>
            )}
          </div>

          <button
            type="button"
            className="hardware-subcategory-arrow hardware-subcategory-arrow-right"
            onClick={() => scrollBrandSlider("right")}
            aria-label="Scroll brands right"
          >
            ›
          </button>
        </div>
      </section>

      <section id="comp-offer" className="subproducts-shop-section">
        <div className="container-fluid">
          <div className="subproducts-shop-layout">
            <aside className="subproducts-filter-sidebar">
              <div className="sp-filter-title">Search Filter</div>

              <div className="sp-filter-block">
                <h4>Search Product</h4>
                <input
                  type="text"
                  className="sp-filter-input"
                  placeholder="Search..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </div>

              <div className="sp-filter-block">
                <h4>Brand</h4>
                {renderBrandFilters("desktop")}
              </div>

              <div className="sp-filter-block">
                <h4>Product Condition</h4>
                {renderConditionFilters("desktop")}
              </div>

              <div className="sp-filter-block">
                <h4>Stock Status</h4>
                <select
                  className="sp-filter-select"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  {stockOptions.map((stock) => (
                    <option value={stock} key={stock}>
                      {toCaptionCase(stock)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sp-filter-block">
                <h4>Service & Promotion</h4>
                {renderPromotionFilters("desktop")}
              </div>

              <div className="sp-filter-block">
                <h4>Price Range</h4>
                <div className="sp-price-row">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="button"
                className="sp-clear-btn"
                onClick={clearAllFilters}
              >
                Clear All
              </button>
            </aside>

            <div className="subproducts-results-area">
              <div
                className={`subproducts-mobile-actions ${
                  selectedSliderBrand ? "has-selected-brand" : ""
                }`}
              >
                <button
                  type="button"
                  className={`sp-mobile-action-btn ${showMobileFilters ? "active" : ""}`}
                  onClick={() => {
                    setShowMobileFilters((prev) => !prev);
                    setShowMobileSort(false);
                  }}
                >
                  Filters
                </button>

                <button
                  type="button"
                  className={`sp-mobile-action-btn ${showMobileSort ? "active" : ""}`}
                  onClick={() => {
                    setShowMobileSort((prev) => !prev);
                    setShowMobileFilters(false);
                  }}
                >
                  Sort By
                </button>

                {selectedSliderBrand ? (
                  <button
                    type="button"
                    className="sp-mobile-action-btn sp-mobile-selected-brand-btn"
                    onClick={clearSelectedSliderBrand}
                    title="Click to remove selected brand"
                  >
                    {selectedSliderBrand.name} ×
                  </button>
                ) : null}
              </div>

              {showMobileFilters && (
                <div className="subproducts-mobile-panel subproducts-mobile-filter-panel">
                  <details open>
                    <summary>Search</summary>
                    <input
                      type="text"
                      className="sp-filter-input"
                      placeholder="Search product..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                  </details>

                  <details>
                    <summary>Brand</summary>
                    {renderBrandFilters("mobile")}
                  </details>

                  <details>
                    <summary>Condition</summary>
                    {renderConditionFilters("mobile")}
                  </details>

                  <details>
                    <summary>Stock</summary>
                    <select
                      className="sp-filter-select"
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      {stockOptions.map((stock) => (
                        <option value={stock} key={stock}>
                          {toCaptionCase(stock)}
                        </option>
                      ))}
                    </select>
                  </details>

                  <details>
                    <summary>Promotion</summary>
                    {renderPromotionFilters("mobile")}
                  </details>

                  <details>
                    <summary>Price</summary>
                    <div className="sp-price-row">
                      <input
                        type="number"
                        placeholder="Min"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </details>

                  <button
                    type="button"
                    className="sp-mobile-clear-btn"
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </button>
                </div>
              )}

              {showMobileSort && (
                <div className="subproducts-mobile-panel subproducts-mobile-sort-panel">
                  <button
                    type="button"
                    className={sortBy === "popular" ? "active" : ""}
                    onClick={() => setSortBy("popular")}
                  >
                    Popular
                  </button>

                  <button
                    type="button"
                    className={sortBy === "latest" ? "active" : ""}
                    onClick={() => setSortBy("latest")}
                  >
                    Latest
                  </button>

                  <button
                    type="button"
                    className={sortBy === "top_sales" ? "active" : ""}
                    onClick={() => setSortBy("top_sales")}
                  >
                    Top Sales
                  </button>

                  {selectedSliderBrand ? (
                    <button
                      type="button"
                      className="sp-selected-brand-toggle"
                      onClick={clearSelectedSliderBrand}
                      title="Click to remove selected brand"
                    >
                      {selectedSliderBrand.name} ×
                    </button>
                  ) : null}

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="popular">Price</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>
              )}

              <div className="subproducts-sort-bar subproducts-desktop-sort-bar">
                <div className="sp-sort-left">
                  <span>Sort by</span>

                  <button
                    type="button"
                    className={sortBy === "popular" ? "active" : ""}
                    onClick={() => setSortBy("popular")}
                  >
                    Popular
                  </button>

                  <button
                    type="button"
                    className={sortBy === "latest" ? "active" : ""}
                    onClick={() => setSortBy("latest")}
                  >
                    Latest
                  </button>

                  <button
                    type="button"
                    className={sortBy === "top_sales" ? "active" : ""}
                    onClick={() => setSortBy("top_sales")}
                  >
                    Top Sales
                  </button>

                  {selectedSliderBrand ? (
                    <button
                      type="button"
                      className="sp-selected-brand-toggle"
                      onClick={clearSelectedSliderBrand}
                      title="Click to remove selected brand"
                    >
                      {selectedSliderBrand.name} ×
                    </button>
                  ) : null}

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="popular">Price</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>

                <div className="sp-sort-count">
                  {filteredSubProducts.length} / {subProducts.length}
                </div>
              </div>

              {filteredSubProducts.length > 0 ? (
                <div className="subproducts-shop-grid">
                  {filteredSubProducts.map((subproduct: any, index: any) => {
                    const discountPercent = getDiscountPercent(subproduct);
                    const imageUrl = getProductImageUrl(subproduct);

                    return (
                      <article
                        key={index}
                        className="subproducts-shop-card wow fadeInUp"
                        data-wow-delay="0.2s"
                      >
                        <div
                          className="sp-card-image"
                          onClick={() => productData(subproduct.id)}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={subproduct.productName}
                              className="img-fluid"
                            />
                          ) : (
                            <span className="sp-no-image">No Image</span>
                          )}

                          {discountPercent ? (
                            <span className="sp-discount-badge">
                              -{discountPercent}%
                            </span>
                          ) : null}

                          {Number(subproduct.is_promoted) === 1 ? (
                            <span className="sp-promoted-badge">Promoted</span>
                          ) : null}
                        </div>

                        <div className="sp-card-body">
                          <div className="sp-label-row">
                            {subproduct.brand_name ? (
                              <span className="sp-brand-label">
                                {subproduct.brand_name}
                              </span>
                            ) : null}

                            <span className="sp-local-label">Local Seller</span>
                          </div>

                          <h3 onClick={() => productData(subproduct.id)}>
                            {subproduct.productName}
                          </h3>

                          <p className="sp-card-desc">
                            {subproduct.shortDescription}
                          </p>

                          <div className="sp-card-bottom">
                            <div>
                              <div className="sp-price">
                                {formatCurrency(subproduct.active_price)}
                              </div>

                              {subproduct.previousPrice &&
                              toSafeNumber(subproduct.previousPrice) >
                                toSafeNumber(subproduct.active_price) ? (
                                <div className="sp-old-price">
                                  {formatCurrency(subproduct.previousPrice)}
                                </div>
                              ) : null}
                            </div>

                            <span className="sp-sold">
                              {toSafeNumber(subproduct.total_sold) > 0
                                ? `${subproduct.total_sold}+ sold`
                                : "New"}
                            </span>
                          </div>

                          <button
                            className="sp-add-cart-btn"
                            type="button"
                            onClick={() =>
                              addToCart({
                                id: subproduct.id,
                                barCode: subproduct.barCode,
                                vendor_id: subproduct.vendor_id,
                                productName: subproduct.productName,
                                active_price: subproduct.active_price,
                                image_path: subproduct.image_path,
                                variant: subproduct.color_name || "Default",
                              })
                            }
                          >
                            Add To Cart
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="sp-no-product-box">
                  <p>NO PRODUCT FOUND</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SubProducts;
